import { Component, forwardRef, Input } from '@angular/core';
import {
  AbstractControl,
  ControlValueAccessor,
  UntypedFormBuilder,
  UntypedFormGroup,
  NG_VALIDATORS,
  NG_VALUE_ACCESSOR,
  ValidationErrors,
  Validator,
} from '@angular/forms';
import {
  OperatorsForNonSettableItem,
  OperatorsForNonVoidItems,
} from '@app/library/models/filters.model';
import { slideInOutBlock } from '@radioking/ui/animations';

@Component({
  selector: 'rk-filter-numeric-input',
  templateUrl: './filter-numeric-input.component.html',
  styleUrls: ['./filter-numeric-input.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => FilterNumericInputComponent),
      multi: true,
    },
    {
      provide: NG_VALIDATORS,
      useExisting: forwardRef(() => FilterNumericInputComponent),
      multi: true,
    },
  ],
  animations: [slideInOutBlock],
})
export class FilterNumericInputComponent implements ControlValueAccessor, Validator {
  numericItemForm: UntypedFormGroup;

  @Input()
  nameHeader: string;

  @Input()
  labelNumberInput: string;

  @Input()
  allText: string;

  @Input()
  equalText: string;

  @Input()
  notEqualText: string;

  @Input()
  lowerText: string;

  @Input()
  greaterText: string;

  @Input()
  betweenText: string;

  @Input()
  notSetText: string;

  @Input()
  skipEqual = false;

  @Input()
  skipNotEquals = false;

  @Input()
  skipNotSet = true;

  constructor(private readonly fb: UntypedFormBuilder) {
    this.numericItemForm = this.fb.group({
      operator: 'none',
      equalInput: 0,
      notEqualInput: 0,
      lessInput: 0,
      overInput: 0,
      betweenLessInput: 0,
      betweenMoreInput: 0,
    });
  }

  onTouched: () => void = () => {};

  writeValue(val: any): void {
    if (!val) {
      return;
    }
    this.numericItemForm.setValue(this.fromFilterToFormValue(val), { emitEvent: false });
  }

  fromFilterToFormValue(
    val: OperatorsForNonSettableItem | OperatorsForNonVoidItems,
  ): any {
    const { operator, value, end, start } = val as any;
    const defaults = {
      operator: 'none',
      equalInput: 0,
      notEqualInput: 0,
      lessInput: 0,
      overInput: 0,
      betweenLessInput: 0,
      betweenMoreInput: 0,
    };
    switch (operator) {
      case 'equals':
        return {
          ...defaults,
          operator,
          equalInput: value,
        };
      case 'not_equals':
        return {
          ...defaults,
          operator,
          notEqualInput: value,
        };
      case 'lower_equals':
      case 'lower':
        return {
          ...defaults,
          operator: 'lower',
          lessInput: value,
        };
      case 'greater_equals':
      case 'greater':
        return {
          ...defaults,
          operator: 'greater',
          overInput: value,
        };
      case 'between':
        return {
          ...defaults,
          operator,
          betweenLessInput: start,
          betweenMoreInput: end,
        };
      case 'not_set':
        return {
          ...defaults,
          operator,
        };
      case 'none':
      default:
        return defaults;
    }
  }

  fromFormValueToFilter(
    val: any,
  ): OperatorsForNonSettableItem | OperatorsForNonVoidItems {
    const {
      operator,
      equalInput,
      notEqualInput,
      lessInput,
      overInput,
      betweenLessInput,
      betweenMoreInput,
    } = val;

    switch (operator) {
      case 'equals':
        return {
          operator,
          value: equalInput,
        };
      case 'not_equals':
        return {
          operator,
          value: notEqualInput,
        };
      case 'lower':
      case 'lower_equals':
        return {
          operator: 'lower',
          value: lessInput,
        };
      case 'greater_equals':
      case 'greater':
        return {
          operator: 'greater',
          value: overInput,
        };
      case 'between':
        return {
          operator,
          start: betweenLessInput,
          end: betweenMoreInput,
        };
      case 'not_set':
        return {
          operator,
        };
      case 'none':
      default:
        return {
          operator: 'none',
        };
    }
  }

  registerOnChange(fn: any): void {
    this.numericItemForm.valueChanges.subscribe(val =>
      fn(this.fromFormValueToFilter(val)),
    );
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  setDisabledState?(isDisabled: boolean): void {
    isDisabled ? this.numericItemForm.disable() : this.numericItemForm.enable();
  }

  validate(_: AbstractControl): ValidationErrors | null {
    return this.numericItemForm.valid
      ? null
      : { invalidForm: { valid: false, message: 'TAGS' } };
  }
}
